import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { setAuthTokens, setUnauthorizedHandler } from '../services/api';
import { authService, type LoginRequest, type RegisterRequest } from '../services/auth.service';
import { config } from '../config/env';
import type { AuthTokens, UserMembership, UserPrincipal } from '../types/data-models';

interface PersistedAuth {
  user: UserPrincipal;
  tokens: AuthTokens;
  memberships: UserMembership[];
}

interface AuthContextValue {
  user: UserPrincipal | null;
  memberships: UserMembership[];
  isAuthenticated: boolean;
  loading: boolean;
  login: (payload: LoginRequest) => Promise<void>;
  register: (payload: RegisterRequest) => Promise<void>;
  /** Sign in with a Google ID token. Rejects accounts that don't exist yet. */
  loginWithGoogle: (idToken: string) => Promise<void>;
  logout: () => void;
  /** Re-authenticate against a different tenant without re-entering a password. */
  switchTenant: (tenantId: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readPersisted(): PersistedAuth | null {
  try {
    const raw = localStorage.getItem(config.authStorageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedAuth>;
    // Drop entries that don't match the current shape (e.g. left over from
    // an older login flow). Otherwise downstream `.tokens.accessToken` reads
    // crash on first render.
    if (!parsed?.user || !parsed?.tokens?.accessToken) {
      localStorage.removeItem(config.authStorageKey);
      return null;
    }
    return parsed as PersistedAuth;
  } catch {
    localStorage.removeItem(config.authStorageKey);
    return null;
  }
}

function writePersisted(value: PersistedAuth | null) {
  if (value) localStorage.setItem(config.authStorageKey, JSON.stringify(value));
  else localStorage.removeItem(config.authStorageKey);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const initial = readPersisted();
  const [user, setUser] = useState<UserPrincipal | null>(initial?.user ?? null);
  const [memberships, setMemberships] = useState<UserMembership[]>(initial?.memberships ?? []);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  // Sync the tokens into the axios interceptor on every change.
  useEffect(() => {
    setAuthTokens(initial?.tokens?.accessToken ?? null, initial?.tokens?.refreshToken ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = useCallback((next: PersistedAuth | null) => {
    writePersisted(next);
    setAuthTokens(next?.tokens?.accessToken ?? null, next?.tokens?.refreshToken ?? null);
    setUser(next?.user ?? null);
    setMemberships(next?.memberships ?? []);
  }, []);

  const login = useCallback(
    async (payload: LoginRequest) => {
      setLoading(true);
      try {
        const res = await authService.login(payload);
        persist({ user: res.user, tokens: res.tokens, memberships: res.memberships ?? [] });
      } finally {
        setLoading(false);
      }
    },
    [persist],
  );

  const register = useCallback(
    async (payload: RegisterRequest) => {
      setLoading(true);
      try {
        const res = await authService.register(payload);
        persist({ user: res.user, tokens: res.tokens, memberships: res.memberships ?? [] });
      } finally {
        setLoading(false);
      }
    },
    [persist],
  );

  const loginWithGoogle = useCallback(
    async (idToken: string) => {
      setLoading(true);
      try {
        const res = await authService.loginWithGoogle(idToken);
        persist({ user: res.user, tokens: res.tokens, memberships: res.memberships ?? [] });
      } finally {
        setLoading(false);
      }
    },
    [persist],
  );

  const logout = useCallback(() => persist(null), [persist]);

  const switchTenant = useCallback(
    async (tenantId: number) => {
      // Server validates membership and issues a fresh tenant-scoped JWT.
      // After the swap, blow away the TanStack-Query cache so every list /
      // dropdown re-fetches under the new tenant scope — otherwise the UI
      // would briefly show stale data from the previous tenant.
      const current = readPersisted();
      if (!current) throw new Error('Not authenticated');
      const res = await authService.switchTenant(tenantId);
      persist({ user: res.user, tokens: res.tokens, memberships: current.memberships });
      queryClient.removeQueries();
    },
    [persist, queryClient],
  );

  useEffect(() => {
    setUnauthorizedHandler(() => logout());
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      memberships,
      isAuthenticated: !!user,
      loading,
      login,
      register,
      loginWithGoogle,
      logout,
      switchTenant,
    }),
    [user, memberships, loading, login, register, loginWithGoogle, logout, switchTenant],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
